// Vencimento do IPVA/licenciamento (mesma regra do servidor, backend/src/services/licenciamento.ts):
//  - VENCIMENTO REAL da placa = dia + mes (calendario do estado: UF + final da placa). Todo ano vence nesse dia.
//  - venc_ipva_licenciamento e a DATA DO ULTIMO PAGAMENTO (pode ser adiantado ou atrasado em relacao ao vencimento real).
// Regra do ciclo: o pagamento cobre o vencimento real MAIS PROXIMO da data paga; o proximo vencimento e esse mesmo dia no ano seguinte.
// Sem o dia: ultimo dia do mes. Sem o mes: data do pagamento + 1 ano. Sem pagamento: o proximo vencimento real.
const DATA_RX = /^\d{4}-\d{2}-\d{2}$/

export const UFS = ["AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO"]

export const hojeISO = (hoje: Date = new Date()): string =>
  `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(hoje.getDate()).padStart(2, "0")}`

const diasDoMes = (ano: number, mes: number) => new Date(ano, mes, 0).getDate()
const iso = (a: number, m: number, d: number) => `${a}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`

// mesmo dia e mes no ano seguinte (29/02 vira 28/02 em ano sem bissexto)
export const maisUmAno = (d: string): string => {
  const a = +d.slice(0, 4) + 1, m = +d.slice(5, 7)
  return iso(a, m, Math.min(+d.slice(8, 10), diasDoMes(a, m)))
}

const entre = (de: string, ate: string): number =>
  Math.round((Date.UTC(+ate.slice(0, 4), +ate.slice(5, 7) - 1, +ate.slice(8, 10)) - Date.UTC(+de.slice(0, 4), +de.slice(5, 7) - 1, +de.slice(8, 10))) / 86400000)

export const diasAte = (isoData: string, hoje: Date = new Date()): number => entre(hojeISO(hoje), isoData)

export const dataBR = (d: string): string => `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`

// dia/mes validos? (29/02 vale: em ano sem bissexto vira 28/02)
export const diaMesValido = (mes: any, dia: any): boolean => {
  const m = Number(mes), d = Number(dia)
  return Number.isInteger(m) && m >= 1 && m <= 12 && Number.isInteger(d) && d >= 1 && d <= (m === 2 ? 29 : diasDoMes(2001, m))
}

export interface VencimentoLic { venc: string | null; calculado: boolean; pagamento: string | null; vencReal: string | null }

export function vencimentoLicenciamento(pagamento: any, mes: any, dia: any = null, hoje: Date = new Date()): VencimentoLic {
  const pag = pagamento && DATA_RX.test(String(pagamento).slice(0, 10)) ? String(pagamento).slice(0, 10) : null
  const m = parseInt(mes, 10), temMes = m >= 1 && m <= 12
  const d = parseInt(dia, 10), temDia = temMes && d >= 1 && d <= 31
  const h = hojeISO(hoje)
  const realEm = (ano: number) => iso(ano, m, temDia ? Math.min(d, diasDoMes(ano, m)) : diasDoMes(ano, m))   // sem o dia: ultimo dia do mes
  if (!temMes) {
    if (pag) return { venc: maisUmAno(pag), calculado: false, pagamento: pag, vencReal: null }
    return { venc: null, calculado: false, pagamento: null, vencReal: null }
  }
  if (pag) {
    // vencimento real mais proximo da data paga (anos vizinhos; empate vai para o mais cedo)
    const ano = +pag.slice(0, 4)
    let melhor = realEm(ano - 1), menor = Math.abs(entre(pag, melhor))
    for (const a of [ano, ano + 1]) { const c = realEm(a), dd = Math.abs(entre(pag, c)); if (dd < menor) { menor = dd; melhor = c } }
    return { venc: maisUmAno(melhor), calculado: !temDia, pagamento: pag, vencReal: realEm(+h.slice(0, 4)) }
  }
  // sem pagamento informado: proximo vencimento real (este ano; se ja passou, o do ano que vem)
  let ano = +h.slice(0, 4)
  if (realEm(ano) < h) ano += 1
  return { venc: realEm(ano), calculado: true, pagamento: null, vencReal: realEm(+h.slice(0, 4)) }
}

// consulta o calendario do estado (servidor) e devolve o texto para mostrar ao usuario
export interface SugestaoCalendario { encontrado: boolean; mes?: number; dia?: number; ano?: number; confirmado?: boolean; fonte?: string }
export const textoSugestao = (uf: string, s: SugestaoCalendario): string =>
  !s.encontrado
    ? `Sem calendário cadastrado para ${uf} e este final de placa. Digite o mês e o dia do vencimento (consulte o Detran).`
    : `Calendário ${uf} ${s.ano}: vence dia ${String(s.dia).padStart(2, "0")}/${String(s.mes).padStart(2, "0")}${s.confirmado ? "" : " — calendário não conferido com o Detran, confirme antes de usar"}`
